/**
 * SECURITY: Input validation schemas using Zod
 * All user inputs MUST be validated against these schemas before database operations
 */

import { z } from 'zod';

// Username validation
export const usernameSchema = z
  .string()
  .min(3, 'Username must be at least 3 characters')
  .max(30, 'Username must be at most 30 characters')
  .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
  .refine(val => !val.startsWith('user_'), 'Username cannot start with "user_"')
  .refine(val => !/^\d+$/.test(val), 'Username cannot be all numbers');

// Bio validation
export const bioSchema = z
  .string()
  .max(500, 'Bio must be at most 500 characters')
  .optional();

// Text content validation (for messages, feedback, etc.)
export const textContentSchema = z
  .string()
  .transform(val => val.trim())
  .refine(val => val.length > 0, 'Content cannot be empty')
  .refine(val => val.length <= 2000, 'Content must be at most 2000 characters');

// Email validation
export const emailSchema = z
  .string()
  .email('Invalid email address')
  .max(255, 'Email must be at most 255 characters')
  .toLowerCase();

// Phone validation (Brazilian format)
export const phoneSchema = z
  .string()
  .regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format')
  .optional();

// City/location validation
export const citySchema = z
  .string()
  .max(100, 'City name must be at most 100 characters')
  .optional();

// Occupation/education validation
export const professionalFieldSchema = z
  .string()
  .max(200, 'Field must be at most 200 characters')
  .optional();

// Interests validation
export const interestsSchema = z
  .array(z.string().max(50))
  .max(20, 'Maximum 20 interests allowed');

// Profile update schema
export const profileUpdateSchema = z.object({
  username: usernameSchema.optional(),
  bio: bioSchema,
  city: citySchema,
  state_code: z.string().max(2).optional(),
  education: professionalFieldSchema,
  occupation: professionalFieldSchema,
  interests: interestsSchema.optional(),
  height: z.string().max(10).optional(),
  body_type: z.string().max(50).optional(),
  hair_color: z.string().max(50).optional(),
  eye_color: z.string().max(50).optional(),
  relationship_type: z.string().max(100).optional(),
  // Security: Never allow client to set these fields
  // joia_level: NOT ALLOWED
  // verification_level: NOT ALLOWED
  // plan_type: NOT ALLOWED
});

// Message content schema
export const messageContentSchema = z.object({
  content: textContentSchema,
  content_type: z.enum(['text', 'image', 'audio']),
  reply_to_message_id: z.string().uuid().optional(),
});

// Support ticket schema
export const supportTicketSchema = z.object({
  subject: z
    .string()
    .min(5, 'Subject must be at least 5 characters')
    .max(200, 'Subject must be at most 200 characters'),
  description: z
    .string()
    .min(20, 'Description must be at least 20 characters')
    .max(5000, 'Description must be at most 5000 characters'),
  type: z.enum(['bug', 'feature', 'question', 'other']),
});

// Document verification schema
export const documentVerificationSchema = z.object({
  phone_number: phoneSchema,
  email_verification_code: z.string().length(6).optional(),
  sms_verification_code: z.string().length(6).optional(),
});

// Connection creation schema
export const connectionSchema = z.object({
  target_id: z.string().uuid('Invalid user ID'),
});

// Helper function to safely validate and return errors
export function validateInput<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: string[] } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  return {
    success: false,
    errors: result.error.issues.map(e => `${e.path.join('.')}: ${e.message}`),
  };
}

// Reward shipment validation schema
export const rewardShipmentSchema = z.object({
  full_name: z
    .string()
    .trim()
    .min(3, 'Digite no mínimo 3 caracteres')
    .max(100, 'Nome muito longo (máximo 100 caracteres)')
    .regex(/^[a-zA-ZÀ-ÿ\s]+$/, 'Digite apenas letras no nome'),
  
  phone: z
    .string()
    .trim()
    .regex(/^\(\d{2}\)\s?\d{4,5}-?\d{4}$/, 'Digite o telefone no formato (11) 99999-9999'),
  
  zip_code: z
    .string()
    .trim()
    .regex(/^\d{5}-?\d{3}$/, 'Digite o CEP no formato 00000-000'),
  
  address: z
    .string()
    .trim()
    .min(10, 'Digite o endereço completo')
    .max(500, 'Endereço muito longo (máximo 500 caracteres)'),
  
  number: z
    .string()
    .trim()
    .min(1, 'Digite o número da residência')
    .max(10, 'Número muito longo (máximo 10 caracteres)'),
  
  complement: z
    .string()
    .trim()
    .max(200, 'Complemento muito longo (máximo 200 caracteres)')
    .optional()
    .or(z.literal('')),
});

export type RewardShipmentInput = z.infer<typeof rewardShipmentSchema>;

// Helper to sanitize HTML content (for display, not storage)
export function sanitizeHtml(html: string): string {
  // Basic HTML sanitization - remove script tags and event handlers
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '');
}
